import { Link } from "react-router-dom";
import { Star, ExternalLink } from "lucide-react";
import { COMPANY } from "@/lib/constants";

interface ReviewBadgeProps {
  variant?: "compact" | "detailed" | "inline";
  showLink?: boolean;
  className?: string;
}

const ReviewBadge = ({ variant = "compact", showLink = true, className = "" }: ReviewBadgeProps) => {
  const renderStars = (count: number = 5) => (
    <div className="flex gap-0.5">
      {Array.from({ length: count }).map((_, i) => (
        <Star key={i} className="w-4 h-4 fill-accent text-accent" />
      ))}
    </div>
  );

  if (variant === "inline") {
    return (
      <div className={`inline-flex items-center gap-2 text-sm ${className}`}>
        {renderStars()}
        <span className="text-muted-foreground">
          {COMPANY.googleRating} ({COMPANY.googleReviews}+ reviews)
        </span>
        {showLink && (
          <Link 
            to="/reviews" 
            className="text-accent hover:underline font-medium"
          >
            See all
          </Link>
        )}
      </div>
    );
  }

  if (variant === "detailed") {
    return (
      <div className={`bg-card border border-border rounded-xl p-4 ${className}`}>
        <div className="flex items-center gap-4">
          {/* Google Icon */}
          <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm border">
            <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
          </div>
          
          {/* Rating Info */}
          <div className="flex-1">
            <div className="flex items-center gap-2">
              {renderStars()}
              <span className="font-bold text-foreground">{COMPANY.googleRating}</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Based on {COMPANY.googleReviews}+ Google reviews
            </p>
          </div>
          
          {/* View All Link */}
          {showLink && (
            <Link 
              to="/reviews"
              className="flex items-center gap-1 text-sm font-medium text-accent hover:underline"
            >
              View All
              <ExternalLink className="w-3 h-3" />
            </Link>
          )}
        </div>
      </div>
    );
  }

  // Compact (default)
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="flex items-center gap-1 bg-secondary rounded-full px-3 py-1.5">
        {renderStars()}
        <span className="font-semibold text-sm text-foreground ml-1">{COMPANY.googleRating}</span>
      </div>
      {showLink && (
        <Link 
          to="/reviews" 
          className="text-sm text-muted-foreground hover:text-accent transition-colors"
        >
          {COMPANY.googleReviews}+ reviews
        </Link>
      )}
    </div>
  );
};

export default ReviewBadge;
